import { useState, useCallback } from 'react';
import { previewSheetsSync, commitSheetsSync } from '../api/sheetsSyncApi';
import type { PreviewResponse, CommitResult } from '../types';
import { isActionableRow } from '../utils/syncHelpers';
import { ApiError } from '../../../services/api/errors/api-error';

export type SyncState =
  | { status: 'idle' }
  | { status: 'previewing' }
  | { status: 'preview-ready'; preview: PreviewResponse; selected: Set<number> }
  | { status: 'committing'; preview: PreviewResponse; selected: Set<number> }
  | { status: 'commit-done'; preview: PreviewResponse; selected: Set<number>; result: CommitResult }
  | { status: 'error'; phase: 'preview' | 'commit'; message: string; preview?: PreviewResponse; selected?: Set<number> };

export interface UseSheetsSync {
  state: SyncState;
  runPreview: (spreadsheetId: string) => Promise<void>;
  toggleRow: (rowNumber: number) => void;
  selectAll: () => void;
  clearAll: () => void;
  commit: () => Promise<void>;
}

export function useSheetsSync(): UseSheetsSync {
  const [state, setState] = useState<SyncState>({ status: 'idle' });

  const runPreview = useCallback(async (spreadsheetId: string) => {
    if (state.status === 'previewing' || state.status === 'committing') return;
    setState({ status: 'previewing' });
    try {
      const preview = await previewSheetsSync(spreadsheetId);
      const selected = new Set(
        preview.rows
          .filter((r) => isActionableRow(r.action))
          .map((r) => r.rowNumber),
      );
      setState({ status: 'preview-ready', preview, selected });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.getUserMessage() : 'Preview failed. Please try again.';
      setState({ status: 'error', phase: 'preview', message });
    }
  }, [state.status]);

  const toggleRow = useCallback((rowNumber: number) => {
    setState((prev) => {
      if (prev.status !== 'preview-ready' && prev.status !== 'commit-done') return prev;
      const next = new Set(prev.selected);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return { ...prev, selected: next };
    });
  }, []);

  const selectAll = useCallback(() => {
    setState((prev) => {
      if (prev.status !== 'preview-ready' && prev.status !== 'commit-done') return prev;
      const allActionable = new Set(
        prev.preview.rows
          .filter((r) => isActionableRow(r.action))
          .map((r) => r.rowNumber),
      );
      return { ...prev, selected: allActionable };
    });
  }, []);

  const clearAll = useCallback(() => {
    setState((prev) => {
      if (prev.status !== 'preview-ready' && prev.status !== 'commit-done') return prev;
      return { ...prev, selected: new Set<number>() };
    });
  }, []);

  const commit = useCallback(async () => {
    if (state.status !== 'preview-ready' && state.status !== 'commit-done') return;
    const { preview, selected } = state;
    if (selected.size === 0) return;

    setState({ status: 'committing', preview, selected });
    try {
      const result = await commitSheetsSync(
        preview.spreadsheetId,
        preview.tabName,
        Array.from(selected),
      );
      setState({ status: 'commit-done', preview, selected, result });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.getUserMessage() : 'Commit failed. Please try again.';
      setState({ status: 'error', phase: 'commit', message, preview, selected });
    }
  }, [state]);

  return { state, runPreview, toggleRow, selectAll, clearAll, commit };
}
